import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Camera, User, BarChart3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageSolverModal } from '@/components/ImageSolverModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LessonsPage = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [solverOpen, setSolverOpen] = useState(false);
  const [olympicsCount, setOlympicsCount] = useState(0);
  const { user, logout, getAuthHeaders } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/subjects/${subjectId}/lessons`, { headers: getAuthHeaders() });
        setLessons(response.data);
      } catch (error) {
        toast.error('فشل في تحميل الدروس');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchLessons();
  }, [subjectId, getAuthHeaders]);

  useEffect(() => {
    axios.get(`${API_URL}/api/olympics/${subjectId}/questions`, { headers: getAuthHeaders() })
      .then(r => setOlympicsCount(r.data?.questions?.length || 0))
      .catch(() => {});
  }, [subjectId, getAuthHeaders]);

  return (
    <div className="llpage" dir="rtl" data-testid="lessons-page">
      {/* Header */}
      <header className="nav" data-testid="main-header">
        <Link to="/grades" className="brand" data-testid="logo-link">RasheedGPT</Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="who" data-testid="user-menu-trigger">
              <i aria-hidden="true">{user?.name?.charAt(0)}</i>
              <span className="hidden sm:inline">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 bg-white border-black/10 text-[#1f1a33]">
            <div className="px-3 py-2 border-b border-black/5">
              <p className="font-semibold text-[#1f1a33]">{user?.name}</p>
              <p className="text-xs text-[#625c78]">{user?.role === 'admin' ? 'مدير' : user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
            </div>
            {user?.role === 'admin' && (
              <DropdownMenuItem asChild className="text-[#4a3aa8] hover:text-[#4a3aa8] hover:bg-black/5 cursor-pointer">
                <Link to="/admin"><BarChart3 className="w-4 h-4 me-2" />لوحة التحكم</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
              <Link to="/grades">الصفوف</Link>
            </DropdownMenuItem>
            {user?.role !== 'admin' && (
              <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
                <Link to="/profile" data-testid="profile-link"><User className="w-4 h-4 me-2" />الملف الشخصي</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setSolverOpen(true)} className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer" data-testid="solve-question-btn">
              <Camera className="w-4 h-4 me-2" />حل سؤال
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-black/5 cursor-pointer" data-testid="logout-button">
              <LogOut className="w-4 h-4 me-2" />تسجيل الخروج
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        <button className="back" onClick={() => navigate(-1)} data-testid="back-button">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          العودة
        </button>

        <div className="hd">
          <span className="badge">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 1l11 11-11 11L1 12z" /></svg>
            {loading ? 'الدروس' : `${lessons.length} ${lessons.length === 2 ? 'درسان' : 'درسًا'}`}
          </span>
          <h1>الدروس</h1>
          <div className="hadith">
            <small>قال رسول الله صلى الله عليه وسلم</small>
            <p>"طلب العلم فريضة على كل مسلم"</p>
          </div>
        </div>

        {loading ? (
          <div className="list">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="ls" key={i} style={{ '--i': i, opacity: 0.5 }}>
                <span className="n">…</span>
                <div className="tx"><h2>…</h2></div>
              </div>
            ))}
          </div>
        ) : lessons.length === 0 ? (
          <div className="list">
            <div className="empty" data-testid="no-lessons">
              <h3 style={{ margin: '0 0 6px', fontWeight: 700, color: 'var(--ink)' }}>لا توجد دروس حالياً</h3>
              <p style={{ margin: 0 }}>سيتم إضافة الدروس قريباً</p>
            </div>
          </div>
        ) : (
          <div className="list" aria-label="الدروس" data-testid="lessons-list">
            {lessons.map((lesson, index) => (
              <Link to={`/lessons/${lesson.id}`} key={lesson.id} className="ls" style={{ '--i': index }} data-testid={`lesson-card-${lesson.id}`}>
                <span className="n">{lesson.order || index + 1}</span>
                <div className="tx">
                  <h2>{lesson.title_ar}</h2>
                  {lesson.title_en && <span>{lesson.title_en}</span>}
                </div>
                <svg className="ch" viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
              </Link>
            ))}
          </div>
        )}

        {/* Olympics Section */}
        {olympicsCount > 0 && (
          <section className="un ol-w" data-testid="olympics-section">
            <div className="uh"><b>تحدَّ نفسك</b></div>
            <button
              className="ls ol"
              onClick={() => navigate(`/olympics/${subjectId}`)}
              style={{ width: '100%', cursor: 'pointer', textAlign: 'right' }}
              data-testid="olympics-btn"
            >
              <span className="tr">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" /></svg>
              </span>
              <div className="tx">
                <h2>أولمبياد رشيد</h2>
                <span>{olympicsCount} سؤال تحدٍّ — اختبر معلوماتك وتنافس مع زملائك!</span>
              </div>
              <svg className="ch" viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
            </button>
          </section>
        )}
      </main>

      <ImageSolverModal open={solverOpen} onOpenChange={setSolverOpen} />
    </div>
  );
};

export default LessonsPage;
